// Spanish (Latin America, tú) copy for /on-demand-ftp. Same shape as copy.en.ts (checked by copy.test.ts).
// "La subida web" is the site's Spanish name for the web uploader; "FTP bajo demanda" as on the pricing pages.
// The uploader's and FileZilla's own labels are in English, so «labels» are quoted as the app shows them.
import type { OnDemandFtpCopy } from "./copy.en";

const es: OnDemandFtpCopy = {
  seo: {
    title: "Cómo funciona el FTP bajo demanda | Photohawk",
    description:
      "Si el FTP es imprescindible, cualquier persona de tu espacio de trabajo puede iniciar su propio servidor FTP de Photohawk desde la subida web, en todos los planes. Listo en unos 30 segundos, con una dirección personal que nunca cambia, y se apaga solo tras una hora sin actividad.",
  },
  hero: {
    badge: "Todos los planes",
    eyebrow: "Ya disponible",
    title: "Cómo funciona el FTP bajo demanda",
    subtitle:
      "Si el FTP es imprescindible, inicia tu propio servidor FTP desde la subida web cuando lo necesites. Está listo en unos 30 segundos y se apaga solo cuando terminas.",
  },
  before: {
    title: "Antes de empezar: la subida web es más rápida",
    body: "La subida web es la forma estándar de subir fotos, en todos los planes.",
    points: [
      { id: "parallel", text: "Sube muchas fotos a la vez, muchas más que una app de FTP como FileZilla (2 a la vez por defecto, y como máximo 10)." },
      { id: "adaptive", text: "Se adapta a tu conexión para aprovecharla al máximo." },
      { id: "live", text: "Primero suben las vistas previas, así tu galería está en línea en minutos mientras llegan los originales." },
      { id: "resume", text: "Si se corta la conexión, se reanuda sola." },
    ],
    button: "Abrir la subida web",
    link: "Conoce la nueva subida web",
  },
  what: {
    heading: "Tu propio servidor FTP, cuando lo necesites",
    body: "Cualquier persona de tu espacio de trabajo puede iniciar su propio servidor FTP desde la subida web, en todos los planes. Cada persona tiene su propio servidor, con una dirección personal que nunca cambia, así que configuras tu app de FTP una sola vez. No hay nada en marcha cuando no lo usas: se apaga solo tras una hora sin actividad.",
    date: "1 de noviembre de 2026",
    closing: "Nuestros servidores FTP compartidos siempre activos cierran el 1 de noviembre de 2026. ¿Los usas hoy? Inicia tu propio servidor y cambia el servidor (host) en tu app de FTP.",
  },
  steps: {
    heading: "Cómo funciona",
    subtitle: "Cuatro pasos la primera vez. Después, inicia tu servidor y sube.",
    items: [
      { id: "open", title: "Abre la subida web", body: "Entra en `upload.photohawk.com` e inicia sesión, o abre la pestaña «Upload» de una galería." },
      { id: "start", title: "Inicia tu servidor", body: "Haz clic en «FTP server», arriba, y luego en «Start FTP server». Está listo en unos 30 segundos." },
      { id: "connect", title: "Copia la dirección y el acceso en tu app de FTP", body: "Tu dirección, el puerto 21, tu correo y tu contraseña de Photohawk. La dirección nunca cambia." },
      { id: "upload", title: "Sube tus fotos", body: "Añade tus fotos a la carpeta de la galería. Se apaga tras una hora sin subir nada." },
    ],
    button: "Inicia tu servidor FTP",
    buttonNote: "Abre la subida web con tu servidor FTP listo para iniciar.",
  },
  panel: {
    heading: "Lo que verás",
    body: "Todo está en el panel «Your FTP server» de la subida web, con un botón para copiar cada dato.",
    points: [
      { id: "address", text: "Tu dirección, como `swift-heron-4821.ftp.photohawk.com`. Es tuya para siempre, así que la configuras una sola vez." },
      { id: "ip", text: "También verás la dirección IP del servidor. Cambia cada vez que lo inicias, así que usa la dirección siempre que puedas." },
      { id: "countdown", text: "Una cuenta atrás hasta que se apague, con «Keep it running» y «Stop»." },
    ],
    alt: "El panel “Your FTP server” en estado Ready en Europe (Ireland), con el servidor swift-heron-4821.ftp.photohawk.com, la dirección IP, el puerto 21, el usuario alex@example.com, el modo pasivo y FTP sobre TLS, y una cuenta atrás con los botones Keep it running y Stop.",
  },
  details: {
    heading: "Tus datos de conexión",
    subtitle: "Escríbelos en tu app de FTP.",
    rows: [
      { id: "host", label: "Servidor (host)", value: "Tu dirección, p. ej. `swift-heron-4821.ftp.photohawk.com` (o la dirección IP que aparece)" },
      { id: "port", label: "Puerto", value: "21" },
      { id: "user", label: "Usuario", value: "Tu correo de Photohawk" },
      { id: "password", label: "Contraseña", value: "Tu contraseña de Photohawk o tu contraseña FTP (más abajo)" },
      { id: "mode", label: "Modo de transferencia", value: "Pasivo" },
      { id: "encryption", label: "Cifrado", value: "FTP simple, o FTPS (FTP explícito sobre TLS). Para FTPS, usa el nombre del servidor, no la dirección IP." },
    ],
    switching: "¿Ya usabas el FTP de Photohawk? Conserva tu usuario, tu contraseña y tus carpetas. Solo cambia el servidor (host) por tu nueva dirección.",
  },
  password: {
    heading: "Una contraseña solo para FTP, si la quieres",
    body: "En lugar de guardar tu contraseña de Photohawk en una app de FTP, haz clic en «Create FTP password» en el panel. Solo funciona en tu servidor FTP, con tu correo como usuario. Puedes copiarla, crear una nueva (la anterior deja de funcionar en menos de un minuto) o eliminarla cuando quieras. Tu contraseña de Photohawk también sigue funcionando.",
  },
  folders: {
    heading: "Tus carpetas",
    body: "Verás las mismas carpetas de siempre:",
    path: "/Galleries/<gallery>/<collection>",
    points: [
      { id: "single", text: "Una galería con una sola colección es simplemente `/Galleries/<gallery>`." },
      { id: "workspaces", text: "¿Subes a más de un espacio de trabajo? Cada uno tiene su propia carpeta, con sus galerías dentro." },
      { id: "create", text: "Crea primero la galería y sus colecciones en Photohawk. Tus fotos aparecen en la galería en unos instantes, igual que con la subida web." },
    ],
  },
  off: {
    heading: "Se apaga solo",
    body: "Tu servidor se apaga tras 1 hora sin actividad, así que no tienes que acordarte de nada. El panel muestra una cuenta atrás.",
    points: [
      { id: "keep", text: "«Keep it running» reinicia la cuenta atrás sin subir nada." },
      { id: "stop", text: "«Stop» lo apaga al instante cuando terminas." },
      { id: "caps", text: "Por seguridad, también se apaga tras 12 horas si no se está subiendo nada, y como máximo tras 24 horas." },
      { id: "again", text: "Vuelve a iniciarlo cuando quieras, con la misma dirección y el mismo acceso." },
    ],
  },
  regions: {
    heading: "Regiones",
    body: "Tu servidor puede funcionar en Europa (Irlanda), el este de EE. UU. (N. Virginia) o Asia-Pacífico (Malasia). Elegimos la más cercana por ti, y puedes cambiarla antes de iniciarlo.",
  },
  filezilla: {
    heading: "Ejemplo: configurar FileZilla",
    subtitle: "Otras apps de FTP usan los mismos ajustes, a veces con nombres un poco distintos.",
    steps: [
      { id: "site", text: "Abre «File → Site Manager» y haz clic en «New site»." },
      { id: "protocol", text: "Protocolo: «FTP – File Transfer Protocol»." },
      { id: "host", text: "Servidor: tu dirección, p. ej. `swift-heron-4821.ftp.photohawk.com`. Puerto: `21`." },
      { id: "tls", text: "Cifrado: «Use explicit FTP over TLS if available»." },
      { id: "login", text: "Modo de acceso: «Normal». Usuario: tu correo de Photohawk. Contraseña: tu contraseña FTP o tu contraseña de Photohawk." },
      { id: "passive", text: "En la pestaña «Transfer Settings», elige «Passive». Haz clic en «Connect» y abre `Galleries`." },
    ],
    tip: "¿Vas a enviar una carpeta grande? FileZilla envía 2 a la vez por defecto, y como máximo 10. Puedes subir su ajuste de transferencias simultáneas hasta 10 («Settings → Transfers»), o usar la subida web, que envía muchas más a la vez.",
  },
  trouble: {
    heading: "Si algo no funciona",
    items: [
      { id: "connect", title: "No puedo conectarme", body: "Comprueba que el panel diga «Ready». Si el nombre del servidor se acaba de crear, usa la dirección IP durante un minuto." },
      { id: "listing", title: "Me conecto, pero la lista de carpetas no carga", body: "Activa el modo pasivo." },
      { id: "logins", title: "“Too many failed logins”", body: "Espera 15 minutos y revisa el usuario (tu correo) y la contraseña." },
    ],
  },
  faq: {
    heading: "Preguntas",
    items: [
      {
        q: "¿Por qué se apagó mi servidor?",
        a: "Se apaga tras una hora sin actividad, para que nunca esté en marcha cuando no lo usas. Vuelve a iniciarlo cuando quieras: la dirección y el acceso no cambian. Para mantenerlo encendido sin subir nada, haz clic en «Keep it running». También se apaga tras 12 horas si no se está subiendo nada, y como máximo tras 24 horas.",
      },
      {
        q: "¿Pueden dos personas tener servidores a la vez?",
        a: "Sí. Cada persona de tu espacio de trabajo puede iniciar su propio servidor, uno cada una, con su propia dirección. Solo tú puedes acceder al tuyo.",
      },
      {
        q: "¿Es seguro?",
        a: "Sí. Con FTPS (FTP sobre TLS) en el nombre de tu servidor, la conexión va cifrada con un certificado de confianza. Solo tu acceso funciona en tu servidor, y tras varios intentos fallidos de inicio de sesión, el servidor bloquea esa conexión durante 15 minutos.",
      },
      {
        q: "¿Tiene un costo adicional?",
        a: "No. Tu propio servidor FTP está incluido en todos los planes.",
      },
      {
        q: "¿Está en todos los planes?",
        a: "Sí, también en Lite. Para el día a día, la subida web o Photohawk Studio son más rápidos; tu propio servidor FTP está ahí cuando el FTP es imprescindible.",
      },
      {
        q: "¿Qué pasa con el antiguo FTP compartido?",
        a: "Los servidores FTP compartidos siempre activos cierran el 1 de noviembre de 2026 y funcionan como siempre hasta entonces. Para seguir usando FTP, inicia tu propio servidor y cambia el servidor (host) en tu app de FTP por tu nueva dirección. Tu usuario, tu contraseña y tus carpetas no cambian.",
      },
    ],
  },
  cta: {
    heading: "Cuando quieras",
    body: "Para el día a día, usa la subida web. Cuando el FTP sea imprescindible, tu propio servidor está a un clic.",
    primary: "Abrir la subida web",
    secondary: "Inicia tu servidor FTP",
  },
};

export default es;
